import React, { useEffect, useMemo, useState } from 'react';
import { apiGet, apiPost, apiPut, getAuthUser } from '../lib/api';
import { Badge } from '../lib/badge';
import Icon from '../components/icons';
import Modal from '../components/Modal';
import { EmptyState } from './ListView';
import { canManage } from '../lib/roles';
import RoleBanner from '../components/RoleBanner';

const ROLES = [
  { id: 'admin', label: 'Admin', hint: 'Billing, catalog, users, credentials' },
  { id: 'operator', label: 'Operator', hint: 'Tasks, routing, approvals' },
  { id: 'viewer', label: 'Viewer', hint: 'Read-only access' },
];

const EMPTY_FORM = { name: '', email: '', role: 'viewer', password: '', is_active: true };

function initials(name, email) {
  const src = String(name || email || '?').trim();
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

function roleTone(role) {
  if (role === 'admin') return 'danger';
  if (role === 'operator') return 'info';
  return 'muted';
}

function formatLogin(ts) {
  if (!ts) return 'Never signed in';
  const d = new Date(ts);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default function Users() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [query, setQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [modal, setModal] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const me = getAuthUser();

  function load() {
    if (!canManage()) {
      setLoading(false);
      setRows([]);
      return;
    }
    setLoading(true);
    setErr('');
    apiGet('/auth/users')
      .then((data) => setRows(Array.isArray(data) ? data : []))
      .catch((e) => setErr(String(e.message || e)))
      .finally(() => setLoading(false));
  }

  useEffect(load, []);

  const stats = useMemo(() => ({
    total: rows.length,
    active: rows.filter((u) => u.is_active).length,
    admins: rows.filter((u) => u.role === 'admin').length,
    disabled: rows.filter((u) => !u.is_active).length,
  }), [rows]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((u) => {
      if (roleFilter !== 'all' && u.role !== roleFilter) return false;
      if (!q) return true;
      return (
        String(u.name || '').toLowerCase().includes(q)
        || String(u.email || '').toLowerCase().includes(q)
        || String(u.role || '').toLowerCase().includes(q)
      );
    });
  }, [rows, query, roleFilter]);

  function openCreate() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setModal('create');
  }

  function openEdit(user) {
    setEditing(user);
    setForm({
      name: user.name || '',
      email: user.email || '',
      role: user.role || 'viewer',
      password: '',
      is_active: !!user.is_active,
    });
    setModal('edit');
  }

  function openPassword(user) {
    setEditing(user);
    setForm({ ...EMPTY_FORM, password: '' });
    setModal('password');
  }

  function update(field) {
    return (e) => {
      const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
      setForm((f) => ({ ...f, [field]: value }));
    };
  }

  async function submitCreate(e) {
    e.preventDefault();
    setBusy(true);
    setErr('');
    try {
      await apiPost('/auth/users', {
        name: form.name.trim(),
        email: form.email.trim(),
        role: form.role,
        password: form.password,
      });
      setModal(null);
      load();
    } catch (ex) {
      setErr(String(ex.message || ex));
    } finally {
      setBusy(false);
    }
  }

  async function submitEdit(e) {
    e.preventDefault();
    if (!editing) return;
    setBusy(true);
    setErr('');
    try {
      await apiPut(`/auth/users/${editing.id}`, {
        name: form.name.trim(),
        role: form.role,
        is_active: form.is_active,
      });
      setModal(null);
      load();
    } catch (ex) {
      setErr(String(ex.message || ex));
    } finally {
      setBusy(false);
    }
  }

  async function submitPassword(e) {
    e.preventDefault();
    if (!editing) return;
    setBusy(true);
    setErr('');
    try {
      await apiPost(`/auth/users/${editing.id}/reset-password`, { password: form.password });
      setModal(null);
    } catch (ex) {
      setErr(String(ex.message || ex));
    } finally {
      setBusy(false);
    }
  }

  async function toggleActive(user) {
    if (!canManage()) return;
    setBusy(true);
    setErr('');
    try {
      await apiPut(`/auth/users/${user.id}`, { is_active: !user.is_active });
      load();
    } catch (ex) {
      setErr(String(ex.message || ex));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="page users-page">
      <div className="page__header page__header--split">
        <div className="page__header-row">
          <span className="page__icon">
            <Icon name="customers" />
          </span>
          <div className="page__header-text">
            <h2>Users</h2>
            <p>Invite teammates and assign Admin, Operator, or Viewer access.</p>
          </div>
        </div>
        {canManage() && (
          <div className="page__header-actions">
            <button type="button" className="btn-primary" onClick={openCreate}>
              <Icon name="customers" />
              Add user
            </button>
          </div>
        )}
      </div>

      <RoleBanner action="manage users" adminOnly />

      {err && <div className="status status--error">{err}</div>}

      {!canManage() ? null : loading ? (
        <p className="cell-muted">Loading users…</p>
      ) : rows.length === 0 ? (
        <EmptyState label="No users yet. Add your first teammate." />
      ) : (
        <>
          <div className="users-stats">
            <div className="users-stat">
              <span className="users-stat__label">Total</span>
              <strong className="users-stat__value">{stats.total}</strong>
            </div>
            <div className="users-stat">
              <span className="users-stat__label">Active</span>
              <strong className="users-stat__value">{stats.active}</strong>
            </div>
            <div className="users-stat">
              <span className="users-stat__label">Admins</span>
              <strong className="users-stat__value">{stats.admins}</strong>
            </div>
            <div className="users-stat">
              <span className="users-stat__label">Disabled</span>
              <strong className="users-stat__value">{stats.disabled}</strong>
            </div>
          </div>

          <div className="users-toolbar">
            <div className="users-search">
              <Icon name="search" />
              <input
                type="search"
                className="users-search__input"
                placeholder="Search by name or email…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <div className="filter-chips">
              <button
                type="button"
                className={`filter-chip ${roleFilter === 'all' ? 'filter-chip--active' : ''}`}
                onClick={() => setRoleFilter('all')}
              >
                All
              </button>
              {ROLES.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  className={`filter-chip ${roleFilter === r.id ? 'filter-chip--active' : ''}`}
                  onClick={() => setRoleFilter(r.id)}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          {filtered.length === 0 ? (
            <EmptyState label="No users match that filter." />
          ) : (
            <div className="users-list">
              {filtered.map((u) => {
                const isMe = me?.id != null && Number(me.id) === Number(u.id);
                return (
                  <article
                    key={u.id}
                    className={`user-card ${u.is_active ? '' : 'user-card--disabled'}`}
                  >
                    <div className="user-card__identity">
                      <div className={`user-card__avatar user-card__avatar--${u.role}`}>
                        {initials(u.name, u.email)}
                      </div>
                      <div className="user-card__meta">
                        <div className="user-card__name-row">
                          <h3>{u.name}</h3>
                          {isMe && <span className="user-card__you">You</span>}
                        </div>
                        <p className="user-card__email">{u.email}</p>
                        <p className="user-card__login">{formatLogin(u.last_login_at)}</p>
                      </div>
                    </div>

                    <div className="user-card__badges">
                      <Badge tone={roleTone(u.role)}>{u.role}</Badge>
                      <Badge tone={u.is_active ? 'success' : 'muted'}>
                        {u.is_active ? 'active' : 'disabled'}
                      </Badge>
                    </div>

                    <div className="user-card__actions">
                      <button type="button" className="user-action" onClick={() => openEdit(u)}>
                        Edit
                      </button>
                      <button type="button" className="user-action" onClick={() => openPassword(u)}>
                        Reset password
                      </button>
                      <button
                        type="button"
                        className={`user-action ${u.is_active ? 'user-action--warn' : 'user-action--ok'}`}
                        disabled={busy || isMe}
                        onClick={() => toggleActive(u)}
                        title={isMe ? 'Cannot deactivate your own account' : undefined}
                      >
                        {u.is_active ? 'Disable' : 'Enable'}
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </>
      )}

      {modal === 'create' && (
        <Modal title="Add user" onClose={() => setModal(null)}>
          <form onSubmit={submitCreate} className="users-form">
            <div className="form-grid">
              <label className="field-label">
                Full name
                <input className="field-input" style={{ width: '100%' }} value={form.name} onChange={update('name')} required />
              </label>
              <label className="field-label">
                Work email
                <input type="email" className="field-input" style={{ width: '100%' }} value={form.email} onChange={update('email')} required />
              </label>
              <label className="field-label">
                Role
                <select className="field-input" style={{ width: '100%' }} value={form.role} onChange={update('role')}>
                  {ROLES.map((r) => (
                    <option key={r.id} value={r.id}>{r.label} — {r.hint}</option>
                  ))}
                </select>
              </label>
              <label className="field-label">
                Temporary password
                <input type="password" className="field-input" style={{ width: '100%' }} value={form.password} onChange={update('password')} required minLength={8} />
              </label>
            </div>
            <button type="submit" className="btn-primary" disabled={busy}>
              {busy ? 'Creating…' : 'Create user'}
            </button>
          </form>
        </Modal>
      )}

      {modal === 'edit' && editing && (
        <Modal title={`Edit ${editing.name}`} onClose={() => setModal(null)}>
          <form onSubmit={submitEdit} className="users-form">
            <p className="users-form__email">{editing.email}</p>
            <div className="form-grid">
              <label className="field-label">
                Full name
                <input className="field-input" style={{ width: '100%' }} value={form.name} onChange={update('name')} required />
              </label>
              <label className="field-label">
                Role
                <select className="field-input" style={{ width: '100%' }} value={form.role} onChange={update('role')}>
                  {ROLES.map((r) => (
                    <option key={r.id} value={r.id}>{r.label} — {r.hint}</option>
                  ))}
                </select>
              </label>
              <label className="users-form__check">
                <input type="checkbox" checked={form.is_active} onChange={update('is_active')} />
                Account is active
              </label>
            </div>
            <button type="submit" className="btn-primary" disabled={busy}>
              {busy ? 'Saving…' : 'Save changes'}
            </button>
          </form>
        </Modal>
      )}

      {modal === 'password' && editing && (
        <Modal title="Reset password" onClose={() => setModal(null)}>
          <form onSubmit={submitPassword} className="users-form">
            <p className="users-form__email">{editing.email}</p>
            <label className="field-label">
              New password
              <input type="password" className="field-input" style={{ width: '100%' }} value={form.password} onChange={update('password')} required minLength={8} />
            </label>
            <button type="submit" className="btn-primary" disabled={busy}>
              {busy ? 'Updating…' : 'Set password'}
            </button>
          </form>
        </Modal>
      )}
    </main>
  );
}
